import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
const _excluded = ["children", "className"],
  _excluded2 = ["children", "domainName", "priority"],
  _excluded3 = ["children", "className"],
  _excluded4 = ["title"];
import { Box, Button, Loader, SkeletonGroup, SkeletonRectangle, TextButton } from '@wix/design-system';
import { DomainSearchSuiteModule, SearchRecommendationLayout } from '@wix/domains-search-suite';
import { DomainsApiClient } from '@wix/domains-serverless-interface/react-kit';
import { ChevronDown, ChevronLeft } from '@wix/wix-ui-icons-common/lazy';
import React, { useCallback, useMemo } from 'react';
import bngDomainSuggestionsStyles from './BngDomainSuggestionsSection.scss';
import BngDomainSuggestionsError from './BngDomainSuggestionsError';
import { BngDomainSuggestionsDataHooks } from './dataHooks';
import { RecommendationsError } from '../../../../Components/Recommendations';
import { LazyRecommendationLabel } from '../../../../Components/LazyRecommedationLabel/LazyRecommendationLabel';
import { useMatchingLabels } from '../../../../hooks/useMatchingLabels';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import { DomainSearchGateModule } from '../../../../module/domainSearchGate.module';
import BngDomainSuggestionsHeading from './BngDomainSuggestionsHeading';
import heroCardStyles from '../../../../Components/HeroCard/HeroCard.css';
const BngSearchRecommendationContainer = _ref => {
  let children = _ref.children,
    className = _ref.className,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const isMobile = useIsMobile();
  return /*#__PURE__*/React.createElement(SearchRecommendationLayout.SearchRecommendationContainer, _extends({}, props, isMobile ? {
    padding: '20px 16px'
  } : {}, {
    className: className,
    minHeight: "114px",
    boxSizing: "border-box"
  }), children);
};
const BngGetItButton = _ref2 => {
  let children = _ref2.children,
    _domainName = _ref2.domainName,
    priority = _ref2.priority,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  return /*#__PURE__*/React.createElement(Button, _extends({
    size: "medium",
    skin: "dark"
  }, rest, {
    priority: priority ?? 'secondary'
  }), children);
};
const BngDomainSuggestionsSection = _ref3 => {
  var _recommendations$;
  let recommendations = _ref3.recommendations,
    isLoading = _ref3.isLoading,
    hasMore = _ref3.hasMore,
    fetchNextPage = _ref3.fetchNextPage,
    isFetchingNextPage = _ref3.isFetchingNextPage,
    hasFetchError = _ref3.hasFetchError,
    onRetry = _ref3.onRetry,
    headingRef = _ref3.headingRef,
    searchTerm = _ref3.searchTerm,
    _ref3$isPricingLoadin = _ref3.isPricingLoading,
    isPricingLoading = _ref3$isPricingLoadin === void 0 ? false : _ref3$isPricingLoadin,
    onBackToNames = _ref3.onBackToNames,
    animateMobileEntrance = _ref3.animateMobileEntrance;
  const isMobile = useIsMobile();
  const _DomainsApiClient$hoo = DomainsApiClient.hooks.useGetPurchasePermissions({}),
    purchasePermissions = _DomainsApiClient$hoo.data;
  const _DomainSearchGateModu = DomainSearchGateModule.internals.useTranslations(),
    t = _DomainSearchGateModu.t;
  const showEmptyState = !isLoading && recommendations.length === 0 && !hasFetchError;
  const _DomainSearchSuiteMod = DomainSearchSuiteModule.SDK.useSuggestionActionResolver(),
    resolveActionForSuggestion = _DomainSearchSuiteMod.resolveActionForSuggestion;
  const _useMatchingLabels = useMatchingLabels(recommendations),
    matchLabels = _useMatchingLabels.labels,
    isLoadingLabels = _useMatchingLabels.isLoading;
  const firstRecommendationDomain = (_recommendations$ = recommendations[0]) == null || (_recommendations$ = _recommendations$.domain) == null ? void 0 : _recommendations$.trim();
  const handleGetItClick = useCallback(recommendation => {
    const position = recommendations == null ? void 0 : recommendations.findIndex(_ref4 => {
      let id = _ref4.id;
      return id === recommendation.id;
    });
    DomainSearchSuiteModule.internals.bi.onGetItClick == null || DomainSearchSuiteModule.internals.bi.onGetItClick({
      domain: recommendation.domain || '',
      type: recommendation.label,
      position
    });
  }, [recommendations]);
  const onClickGetIt = useCallback(async recommendation => {
    handleGetItClick(recommendation);
    if (recommendation.domain) {
      await resolveActionForSuggestion(recommendation.domain, recommendation.premium);
    }
  }, [handleGetItClick, resolveActionForSuggestion]);
  const handleShowMoreClick = useCallback(() => {
    DomainSearchGateModule.internals.bi.onBngGeneralAction == null || DomainSearchGateModule.internals.bi.onBngGeneralAction({
      action: 'select',
      button_name: 'show_more',
      domain: searchTerm,
      search_term: searchTerm
    });
    fetchNextPage();
  }, [fetchNextPage, searchTerm]);
  const LabelContainerComponent = useMemo(() => LazyRecommendationLabel({
    isLoading: isLoadingLabels,
    matchLabels
  }), [isLoadingLabels, matchLabels]);
  const HoverBox = useMemo(() => _ref5 => {
    let children = _ref5.children,
      className = _ref5.className,
      rest = _objectWithoutPropertiesLoose(_ref5, _excluded3);
    return /*#__PURE__*/React.createElement(Box, _extends({}, rest, {
      display: "block",
      className: className
    }), children);
  }, []);
  const BngSkeleton = useMemo(() => _ref6 => {
    let useMobileLayout = _ref6.useMobileLayout;
    return /*#__PURE__*/React.createElement(SearchRecommendationLayout.Skeleton, {
      limit: 7,
      useMobileLayout: useMobileLayout
    });
  }, []);
  const BngTitleDisplay = useMemo(() => _ref7 => {
    let title = _ref7.title,
      props = _objectWithoutPropertiesLoose(_ref7, _excluded4);
    return /*#__PURE__*/React.createElement(SearchRecommendationLayout.TitleDisplay, _extends({}, props, {
      title: title,
      titleClassName: title.trim() === firstRecommendationDomain ? bngDomainSuggestionsStyles.bngFirstRecoTitle : undefined
    }));
  }, [firstRecommendationDomain]);
  const BngMobileActions = useMemo(() => _ref8 => {
    let pricing = _ref8.pricing,
      getItButton = _ref8.getItButton,
      aiInsightsButton = _ref8.aiInsightsButton;
    return /*#__PURE__*/React.createElement(Box, {
      direction: "vertical",
      gap: "SP4",
      width: "100%"
    }, /*#__PURE__*/React.createElement(Box, {
      direction: "horizontal",
      align: "space-between",
      verticalAlign: "bottom",
      gap: "SP4",
      width: "100%"
    }, /*#__PURE__*/React.createElement(Box, {
      flex: 1
    }, pricing), /*#__PURE__*/React.createElement(Box, {
      flexShrink: 0
    }, getItButton)), aiInsightsButton);
  }, []);
  const layouts = useMemo(() => ({
    LabelContainer: LabelContainerComponent,
    SearchRecommendationContainer: BngSearchRecommendationContainer,
    SearchRecommendationSkeletonContainer: BngSearchRecommendationContainer,
    TitleDisplay: BngTitleDisplay,
    GetItButton: BngGetItButton,
    HoverBox,
    Skeleton: BngSkeleton,
    MobileActions: BngMobileActions
  }), [LabelContainerComponent, BngTitleDisplay, HoverBox, BngSkeleton, BngMobileActions]);
  return /*#__PURE__*/React.createElement(Box, {
    gridArea: "content",
    direction: "vertical",
    width: "100%",
    maxWidth: "980px",
    margin: "0 auto",
    paddingTop: onBackToNames ? '0px' : isMobile ? 'SP4' : '0px',
    paddingBottom: "20px",
    boxSizing: "border-box",
    className: [bngDomainSuggestionsStyles.bngDomainSuggestionsRoot, animateMobileEntrance ? bngDomainSuggestionsStyles.bngMobileStepEnter : undefined].filter(Boolean).join(' '),
    dataHook: BngDomainSuggestionsDataHooks.domainSuggestionsSection
  }, onBackToNames && /*#__PURE__*/React.createElement(Box, {
    marginBottom: "SP4"
  }, /*#__PURE__*/React.createElement(TextButton, {
    onClick: onBackToNames,
    prefixIcon: /*#__PURE__*/React.createElement(ChevronLeft, null),
    size: "small",
    weight: "thin",
    skin: "dark",
    dataHook: "bng-back-to-names"
  }, t('domains_search_gate.bng.mobile.results.name_suggestion.back_button'))), !hasFetchError && /*#__PURE__*/React.createElement(BngDomainSuggestionsHeading, {
    ref: headingRef,
    isLoading: isLoading
  }), hasFetchError ? /*#__PURE__*/React.createElement(BngDomainSuggestionsError, {
    onRetry: onRetry
  }) : showEmptyState ? /*#__PURE__*/React.createElement(RecommendationsError, {
    error: "NoRecommendations",
    onRetry: onRetry,
    type: "anonymous",
    width: "100%"
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DomainSearchSuiteModule.SDK.Components.SearchRecommendationList, {
    disabled: !(purchasePermissions != null && purchasePermissions.hasPermission),
    onClickGetIt: onClickGetIt,
    Layouts: layouts,
    items: recommendations,
    isLoading: isLoading,
    isPricingLoading: isPricingLoading,
    useMobileLayout: isMobile,
    primaryBadgeOnlyForFirst: true,
    renderLastDivider: true
  }), (isLoading || hasMore) && /*#__PURE__*/React.createElement(Box, {
    flex: 1,
    align: "center",
    direction: "vertical",
    padding: "SP6 SP4 SP10 SP4"
  }, isLoading ? /*#__PURE__*/React.createElement(SkeletonGroup, {
    skin: "light",
    className: heroCardStyles.skeletonGroup
  }, /*#__PURE__*/React.createElement(SkeletonRectangle, {
    className: heroCardStyles.skeleton,
    width: "168px",
    height: "18px",
    marginTop: "20px"
  })) : /*#__PURE__*/React.createElement(TextButton, {
    skin: "dark",
    onClick: handleShowMoreClick,
    marginTop: "20px",
    suffixIcon: isFetchingNextPage ? /*#__PURE__*/React.createElement(Box, {
      marginLeft: "3px",
      verticalAlign: "middle"
    }, /*#__PURE__*/React.createElement(Loader, {
      size: "tiny"
    })) : /*#__PURE__*/React.createElement(ChevronDown, null)
  }, t('domain_search_gate.results.show_more')))));
};
export default BngDomainSuggestionsSection;
//# sourceMappingURL=BngDomainSuggestionsSection.js.map